import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'
import Tooltip from './Tooltip'
import { isNil } from 'lodash'

export const buttonStyle = cva(
    [
        'flex items-center justify-center',
        'py-1',
        'px-3',
        'border-0.5',
        'rounded-2',
        'text-base',
        'font-normal',
        'leading-none',
        'hover:cursor-pointer',
        'transition-all duration-300',
        'disabled:cursor-not-allowed',
        'disabled:bg-backgroundDisabled',
        'disabled:border-borderDisabled',
        'disabled:text-textDisabled',
        'whitespace-nowrap'
    ],
    {
        variants: {
            intent: {
                primary: 'bg-buttonPrimary border-borderTertiary text-textPrimaryButton',
                brand: 'bg-backgroundPositiveStrong border-backgroundPositiveStrong text-white hover:opacity-90',
                secondary: 'bg-backgroundSecondary text-textPrimary border-borderPrimary hover:bg-buttonSecondaryHover',
                negative:
                    'bg-backgroundNegativeLight text-textNegative border-backgroundNegativeLight hover:border-borderNegative',
                transparent: 'p-0! w-fit! bg-transparent text-textPrimary border-none hover:text-textSecondary',
                ghostPrimary: 'bg-transparent text-textInformation border-0 hover:underline',
                link: 'p-0! w-fit! text-textLink border-none hover:underline'
            },
            size: {
                square: 'min-w-8 min-h-8 w-8 h-8 p-0!',
                small: 'h-6',
                medium: 'h-8',
                large: 'h-10'
            }
        },
        defaultVariants: {
            intent: 'primary',
            size: 'medium'
        }
    }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
    VariantProps<typeof buttonStyle> & { tooltip?: ReactNode }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, intent, size, tooltip, type = 'submit', ...props }, forwardRef) => {
        const button = (
            <button
                // eslint-disable-next-line react/button-has-type -- the caller sets the type. The default stays the native submit
                type={type}
                ref={forwardRef}
                className={buttonStyle({
                    size,
                    intent,
                    className
                })}
                {...props}
            />
        )

        if (isNil(tooltip)) {
            return button
        }

        return <Tooltip content={tooltip} trigger={button} isActive={!isNil(tooltip)} />
    }
)

export default Button
